import memoizeOne from 'memoize-one';
import qs from 'query-string';
import { createSessionStorageProvider } from '@atlassian/jira-browser-storage-providers/src/controllers/session-storage/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { State } from '../types.tsx';

const SESSION_KEY = 'navigation-journey';
const RELEVANT_PARAMS = ['filter', 'selectedItem', 'rapidView', 'view', 'selectPageId', 'chart'];

const storage = memoizeOne(() => createSessionStorageProvider('page-container'));

// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
const getReferrer = () => (document ? document.referrer : '');

export const isNewJourney = (host: string): boolean => {
	const isComingFromSameHost = getReferrer().includes(host);
	// three situation where isComingFromSameHost will be false:
	// 1. the url change is from spa transition, document.referrer = '',
	// But in this case, we get the state from navJourneyState, this code will not run
	// 2. we navigate from a different host
	// 3. we enter the url directly or open the tab via bookmark, document.referrer = ''
	return !isComingFromSameHost;
};

export const restoreFromSession = (isNew: boolean) => {
	let journey = null;
	// protect from tab reuse
	if (isNew) {
		return journey;
	}
	try {
		journey = JSON.parse(storage().get(SESSION_KEY));
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (err: any) {
		/* ignore */
	}

	return journey;
};

export const saveToSession = (value: State): void => {
	try {
		storage().set(SESSION_KEY, JSON.stringify(value));
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (err: any) {
		/* ignore */
	}
};

export const cleanUrl = (pathname: string, params: string) => {
	let url = pathname.replace(/\/$/, '');
	// ignore /browse redirects or sw classic redirects
	if (/\/browse\/\w+$/.test(pathname) || params.includes('useStoredSettings')) {
		return null;
	}

	// ignore PIN + issue key urls (we only care about filter)
	// issue key is no longer a path param for PIN. This logic can be removed on FG cleanup
	if (!fg('jira_list_nin_merge') && /\/projects\/\w+\/issues\/\w+-\d+/.test(pathname)) {
		url = pathname.replace(/issues\/\w+-\d+(.*)$/, 'issues');
	}

	url += '?';
	if (params) {
		const paramsObj = qs.parse(params);
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const validParams = Object.keys(paramsObj).reduce<Record<string, any>>((acc, pk) => {
			if (RELEVANT_PARAMS.includes(pk)) acc[pk] = paramsObj[pk];
			return acc;
		}, {});
		// ignore GIN issue key change (we only care about filter/jql)
		if (paramsObj.filter || paramsObj.jql) {
			url = url.replace(/^\/issues\?$|^\/browse\/\w+-\d+(.*)$/, '/browse/ISSUE-1?');
		}

		url += qs.stringify(validParams);
	}
	return url;
};
